import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SlotImage from '@/components/base/SlotImage';
import { useCollection } from '@/hooks/useCollection';
import type { NewsArticle } from '@/lib/types';

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function NewsPage() {
  const { rows: articles, loading, error, reload } = useCollection<NewsArticle>('news', 'published_on', false);

  useEffect(() => {
    document.title = 'News & Updates — TGMCI Stories & Announcements';
    window.scrollTo(0, 0);
  }, []);

  const featured = articles.find((a) => a.is_featured) ?? articles[0];
  const rest = articles.filter((a) => a.id !== featured?.id);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900">
            <SlotImage
              slot="news_hero"
              fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/ff81ec1f-f688-499e-9bb3-724007ada18b_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-7.webp"
              alt="News background"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative z-10 max-w-[800px] mx-auto px-4 text-center">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">News &amp; Stories</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Inspiring Stories and Community Updates
            </h1>
            <div className="w-12 h-1 bg-accent-500 mx-auto mb-6" />
            <p className="text-white/60 text-base leading-relaxed">
              Stay updated with the latest news, stories, and announcements from TGMCI and our ministry programs.
            </p>
          </div>
        </section>

        {loading ? (
          <div className="py-20 text-center text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
            <p className="mt-2 text-sm">Loading news...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <p className="text-red-600 text-sm mb-3">Could not load news.</p>
            <button type="button" onClick={reload} className="btn-primary">Retry</button>
          </div>
        ) : (
          <>
            {/* Featured Article */}
            {featured && (
              <section className="py-16 bg-background-50">
                <div className="max-w-[1200px] mx-auto px-4 md:px-8">
                  <h2 className="font-heading text-xl font-semibold text-foreground-700 mb-6 flex items-center gap-2">
                    <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                    Featured Story
                  </h2>
                  <article className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-background-100 rounded-2xl overflow-hidden border border-background-200">
                    <div className="h-64 lg:h-auto img-zoom overflow-hidden">
                      <img
                        src={featured.cover_image}
                        alt={featured.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <span className="text-xs font-label font-semibold bg-accent-100 text-accent-800 px-2.5 py-1 rounded-full">
                          {featured.category}
                        </span>
                        <span className="text-xs text-foreground-400">{formatDate(featured.published_on)}</span>
                        {featured.read_time && <span className="text-xs text-foreground-400">{featured.read_time}</span>}
                      </div>
                      <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
                        {featured.title}
                      </h3>
                      <p className="text-foreground-500 text-base leading-relaxed mb-6">{featured.excerpt}</p>
                      <Link to="/news" className="btn-primary self-start">
                        Read Full Story
                        <i className="ri-arrow-right-line" aria-hidden="true" />
                      </Link>
                    </div>
                  </article>
                </div>
              </section>
            )}

            {/* More Articles */}
            {rest.length > 0 && (
              <section className="py-10 pb-24 bg-background-50">
                <div className="max-w-[1200px] mx-auto px-4 md:px-8">
                  <h2 className="font-heading text-xl font-semibold text-foreground-700 mb-6 flex items-center gap-2">
                    <i className="ri-news-line text-primary-600" aria-hidden="true" />
                    More Stories
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((article) => (
                      <article
                        key={article.id}
                        className="group bg-background-100 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 hover:-translate-y-1 transition-all"
                      >
                        <div className="h-48 overflow-hidden img-zoom">
                          <img
                            src={article.cover_image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="p-5">
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <span className="text-xs font-label font-semibold bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full">
                              {article.category}
                            </span>
                            <span className="text-xs text-foreground-400">{formatDate(article.published_on)}</span>
                            {article.read_time && <span className="text-xs text-foreground-400 ml-auto">{article.read_time}</span>}
                          </div>
                          <h3 className="font-heading font-semibold text-base text-foreground-800 leading-tight mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-foreground-500 text-sm leading-relaxed line-clamp-3 mb-4">{article.excerpt}</p>
                          <Link
                            to="/news"
                            className="inline-flex items-center gap-1 text-sm font-label font-medium text-primary-600 hover:text-primary-800 transition-colors"
                          >
                            Read Post
                            <i className="ri-arrow-right-line text-xs" aria-hidden="true" />
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* CTA Banner */}
        <section className="py-16 bg-primary-700 text-center px-4">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Stay Updated with TGMCI</h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">
            Subscribe to our newsletter to receive the latest news, sermons, and community updates directly in your inbox.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-accent">
              <i className="ri-mail-line" aria-hidden="true" />
              Subscribe to Updates
            </Link>
            <Link to="/give" className="btn-outline-white">
              Support TGMCI
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}